import type React from 'react'
import { useDispatch } from 'react-redux'
import Actions from '../../Flux/index.js'
import { tourSlice } from '../../Flux/Slice/Tour/TourSlice.js'
import { tourMap, tourMeta } from './tourMeta.js'
import type { TourId } from './tourMeta.js'
import type { TourCategories } from './tourCategoryMeta.js'
import { tourCategories } from './tourCategoryMeta.js'

/**
 * @deprecated The guided tours are broken and unused. Kept, not maintained, until we decide whether to revive
 * them: their steps point at top-bar elements that have since moved or gone.
 */
export const TourLanding: React.FC = () => {
  // Group tours by category
  const categorizedTours: Record<TourCategories, TourId[]> = {
    Basics: [],
    Projects: [],
    Admin: [],
    Orders: []
  }
  for (const tourId in tourMeta) {
    const { category } = tourMeta[tourId as TourId]
    categorizedTours[category].push(tourId as TourId)
  }

  const categorizedTourEntries = Object.entries(categorizedTours) as [TourCategories, TourId[]][]

  return (
    <>
      <div className="bg-slate-100 rounded-lg p-6 drop-shadow-sm mx-auto max-w-5xl">
        <h1 className="text-3xl font-semibold mb-4">Spcfy Help Center</h1>
        <p className="mb-6 text-gray-700">
          Welcome to the Spcfy Help Center. Below are guided tours that walk you through important parts of the platform. Click a tour to
          begin.
        </p>

        <div className="space-y-6">
          {categorizedTourEntries
            .sort(([category]: [TourCategories, TourId[]]) => tourCategories[category].priority)
            .map(([category, tourIds]: [TourCategories, TourId[]]) => {
              const catMeta = tourCategories[category]
              return (
                <div key={category}>
                  <h2 className="text-xl font-semibold text-gray-800">{catMeta?.label ?? category}</h2>
                  {catMeta?.description && <p className="text-sm text-gray-500 mb-2">{catMeta.description}</p>}
                  <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                    {tourIds.map((tourId) => (
                      <TourCard key={tourId} tourId={tourId} />
                    ))}
                  </div>
                </div>
              )
            })}
        </div>
      </div>
      <div className="fixed bottom-6 right-6 z-50 flex items-end gap-2">
        <div className="bg-white rounded-lg shadow-lg p-3 text-sm max-w-xs">
          <p className="font-semibold">Hi, I&#39;m Wolbachi!</p>
          <p>We’ll see each other around a lot.</p>
        </div>
        <img src={'wolbachi-web.png'} alt="Wolbachi the mascot" className="h-24 w-auto" />
      </div>
    </>
  )
}

interface TourCardProps {
  tourId: TourId
}

const TourCard: React.FC<TourCardProps> = ({ tourId }) => {
  const dispatch = useDispatch()
  const meta = tourMeta[tourId]
  const enabled = (tourMap[tourId]?.length ?? 0) >= 1

  const handleStart = () => {
    if (enabled) {
      dispatch(Actions[tourSlice.name].startTour(tourId))
    }
  }

  return (
    <div
      onClick={handleStart}
      className={`relative border rounded-lg p-4 transition duration-200 
        ${enabled ? 'cursor-pointer bg-white hover:bg-slate-50 shadow-sm' : 'bg-gray-100 text-gray-400 pointer-events-none select-none'}`}
    >
      {/* Diagonal "Coming Soon" Ribbon */}
      {!enabled && <div className="absolute top-0 right-0 bg-yellow-400 text-white text-xs font-bold px-2 py-1 shadow-sm">Coming Soon</div>}

      <div className="flex items-center gap-2 mb-2 text-lg font-medium text-gray-800">
        {meta.icon && <div className={`text-xl ${!enabled ? 'text-gray-400' : ''}`}>{meta.icon}</div>}
        {meta.label}
      </div>
      <div className="text-sm text-gray-600">{meta.description}</div>
    </div>
  )
}
